import { of as observableOf } from "rxjs";
import { map, mergeMap, first, timeoutWith } from "rxjs/operators";
import { GeoCoords } from "../geo/GeoCoords";
import { GeoFrame } from "../geoframe/geoframe";
import { Vector3 } from "three";
import { UtilsBase as Utils } from "../api/Infra3DDataProviderBase";
export class LookAtService {
    constructor(stateService, api, graphService, navigator, jumpNodeCalculator) {
        this._api = api;
        this._stateService = stateService;
        this._graphService = graphService;
        this._currentNode$ = this._stateService.currentNode$;
        this._geoCoords = new GeoCoords();
        this._navigator = navigator;
        this._jumpNodeCalculator = jumpNodeCalculator;
    }
    lookAtView$(xyz, maxdist, epsg, currentnode) {
        maxdist = maxdist || 20;
        if (this._scene.getCurrentGeoframe()) {
            epsg = epsg || this._scene.getCurrentGeoframe().srid;
        }
        else {
            epsg = epsg || 4326;
        }
        const lookatposition = {
            easting: xyz.easting,
            northing: xyz.northing,
            height: xyz.height,
            epsg: epsg,
            maxdist: maxdist,
        };
        const lookat_latlon = this._xyzToLatLon(xyz, epsg);
        return this._lookAtView$(lookatposition, lookat_latlon, lookat_latlon, currentnode).pipe(mergeMap((nodes) => {
            if (nodes.length == 0) {
                return this._api.imagesByKNN$(lookat_latlon.lon, lookat_latlon.lat, 4326).pipe(mergeMap((nearest_node) => {
                    return this._lookAtView$(lookatposition, lookat_latlon, nearest_node.l, currentnode);
                }));
            }
            else {
                return observableOf(nodes);
            }
        }));
    }
    movePosLookAt$(xyz, maxdist, epsg) {
        maxdist = maxdist || 20;
        return this._currentNode$.pipe(first(), timeoutWith(1, observableOf(null)), mergeMap((currentnode) => {
            return this.lookAtView$(xyz, maxdist, epsg || currentnode.frame.epsg, currentnode).pipe(map((nodes) => {
                const latlon = this._xyzToLatLon(xyz, epsg || currentnode.frame.epsg);
                const lookat_nodes = nodes.map((x) => x.node);
                const potential_nodes = this._jumpNodeCalculator.getPotentialJumpNodes(lookat_nodes, currentnode, latlon);
                const jump_to_key = this._jumpNodeCalculator.computeSimilarNode(potential_nodes);
                return nodes.filter((x) => x.node.key == jump_to_key);
            }), mergeMap((nodes) => {
                if (nodes[0]) {
                    const latlon = this._xyzToLatLon(xyz, epsg || nodes[0].node.frame.epsg);
                    const xy_project_epsg = Utils.projectedTo(latlon, this._navigator.epsg || nodes[0].node.frame.epsg);
                    const xyz_project_epsg = {
                        easting: xy_project_epsg.lon,
                        northing: xy_project_epsg.lat,
                        height: xyz.height || nodes[0].node.frame.height - nodes[0].node.frame.capturesystem.height,
                    };
                    return this.moveToKeyAndLookAt$(nodes[0].node.key, xyz_project_epsg, maxdist, 
                    // The following formula was found by interpolating some points and fitting a function
                    // like this the zoomwindowsize is dependent of the distance, which leads to better results than a fix size ;)
                    nodes[0].mapinfo.distance * 0.5 + 3);
                }
                else {
                    throw new Error("Couldn't find a matching Node at the given position!");
                }
            }));
        }));
    }
    moveToKeyAndLookAt$(key, lookat_xyz, maxdist, zoomwindowsize, pointto) {
        maxdist = maxdist || 20;
        zoomwindowsize = zoomwindowsize || 10;
        return this._navigator.moveToKey$(key).pipe(mergeMap((node) => {
            if (node.key == this._scene.getCurrentGeoframe().key)
                return observableOf([this._scene.getCurrentGeoframe(), node]);
            return this._scene.userinputapi.geoframechanged$.pipe(first(), map((geoframe) => [geoframe, node]));
        }), map(([geoframe, node]) => {
            if (lookat_xyz) {
                pointto = {
                    type: "coordinate",
                    coords: [lookat_xyz.easting, lookat_xyz.northing, lookat_xyz.height],
                    maxdist: 2 * maxdist,
                    zoomwindowsize: zoomwindowsize,
                };
            }
            switch (pointto.type) {
                case "coordinate": {
                    const pointto_zoom = pointto;
                    geoframe.zoomToCenterPoint(pointto_zoom.coords, 1, pointto_zoom.maxdist, pointto_zoom.zoomwindowsize);
                    break;
                }
                case "pano": {
                    const pointto_latlon = pointto;
                    geoframe.pointTolatlonfov(pointto_latlon);
                    break;
                }
                case "panzoom": {
                    const pointto_panzoom = pointto;
                    geoframe.pointTopanzoom(pointto_panzoom);
                    break;
                }
            }
            this._scene.infrascene.render();
            return [node, geoframe];
        }));
    }
    moveToKeyAndPointTo$(key, pointto) {
        return this.moveToKeyAndLookAt$(key, undefined, undefined, undefined, pointto).pipe(mergeMap(([node, geoframe]) => observableOf(node)));
    }
    setScene(scene) {
        this._scene = scene;
    }
    _lookAtView$(lookatposition, lookat_latlon, nearest_latlon, currentnode) {
        const sw = this._geoCoords.enuToGeodetic(-20, -20, 0, nearest_latlon.lat, nearest_latlon.lon, 0);
        const ne = this._geoCoords.enuToGeodetic(20, 20, 0, nearest_latlon.lat, nearest_latlon.lon, 0);
        return this._graphService.cacheBoundingBox$({ lat: sw[0], lon: sw[1] }, { lat: ne[0], lon: ne[1] }).pipe(map((node_candidates) => {
            if (currentnode)
                return node_candidates.filter((x) => x.frame.collection == currentnode.frame.collection);
            return node_candidates;
        }), map((node_candidates) => {
            let xy_project_epsg;
            if (node_candidates.length > 0) {
                const project_epsg = this._scene.viewer.epsg || node_candidates[0].frame.epsg;
                xy_project_epsg = Utils.projectedTo(lookat_latlon, project_epsg);
            }
            const imageinfoarray = [];
            for (const node_candidate of node_candidates) {
                let height = lookatposition.height;
                if (!lookatposition.height) {
                    height = node_candidate.frame.height - node_candidate.frame.capturesystem.height;
                }
                const p = new Vector3(xy_project_epsg.lon, xy_project_epsg.lat, height);
                const geoframe = new GeoFrame(this._scene, node_candidate.frame);
                const mapinfo = this._scene
                    .getMeasureHandler()
                    .worldToMapinfo(p, geoframe, 1, lookatposition.maxdist * 2);
                mapinfo.sort((a, b) => a.sensorid - b.sensorid);
                if (mapinfo.length > 0 && mapinfo[0].cubefaceindex !== 5) {
                    imageinfoarray.push({
                        node: node_candidate,
                        mapinfo: mapinfo[0],
                    });
                }
            }
            return imageinfoarray;
        }), map((data) => {
            let potential_jumps = [];
            for (const potential_node of data) {
                const pm = potential_node.mapinfo;
                const score = Math.abs(pm.distance - 8.0) / lookatposition.maxdist +
                    (Math.abs(0.5 - pm.coords.y) < 0.4 ? 0 : 1) +
                    (Math.abs(0.5 - pm.coords.x) < 0.4 ? 0 : 1);
                potential_jumps.push({
                    score: score,
                    node: potential_node.node,
                    mapinfo: pm,
                });
            }
            const total_score = 3;
            potential_jumps = potential_jumps.filter((x) => x.score <= total_score);
            potential_jumps.sort((a, b) => a.score - b.score);
            return potential_jumps.map((x) => {
                return {
                    node: x.node,
                    mapinfo: x.mapinfo,
                };
            });
        }));
    }
    _xyzToLatLon(xyz, epsg) {
        if (this._scene.getCurrentGeoframe()) {
            epsg = epsg || this._scene.getCurrentGeoframe().srid;
        }
        else {
            epsg = epsg || 4326;
        }
        return Utils.projectedFrom({ lon: xyz.easting, lat: xyz.northing }, epsg);
    }
    setMap(map) {
        this._map = map;
    }
}
//# sourceMappingURL=LookAtService.js.map